/**
 * Flatten the nested RbacActions tree into a searchable list with category paths.
 *
 * The action picker in PermissionConditionBuilder needs a flat list of
 * { value: action, label: "Category > Subcategory > Action" } for the searchable
 * select. Recurse through the tree, building the path as we go.
 */

import {
	isRbacAction,
	isRbacActionGroup,
	RbacActions,
} from '@supply-mover-2-0/shared';

export interface FlatRbacAction {
	/** The action string used by the permission system (e.g. "clients:list:any") */
	action: string;
	/** Short label from the action definition (e.g. "List All Clients") */
	label: string;
	/** Full breadcrumb path through the action tree for display in the dropdown */
	pathLabel: string;
	description: string;
}

function walk(
	node: unknown,
	pathParts: Array<string>,
	out: Array<FlatRbacAction>,
): void {
	if (node == null || typeof node !== 'object') {
		return;
	}

	if (isRbacAction(node)) {
		out.push({
			action: node.action,
			label: node.label,
			pathLabel:
				pathParts.length > 0
					? `${pathParts.join(' › ')} › ${node.label}`
					: node.label,
			description: node.description,
		});
		return;
	}

	if (isRbacActionGroup(node)) {
		const nextPath = [...pathParts, node.label];
		for (const [key, value] of Object.entries(node)) {
			if (key === 'label') {
				continue;
			}
			walk(value, nextPath, out);
		}
		return;
	}

	// Top-level RbacActions object — not a group itself, just an index of groups.
	for (const value of Object.values(node as Record<string, unknown>)) {
		walk(value, pathParts, out);
	}
}

// Computed once — RbacActions is a static module-level constant.
const ALL_ACTIONS: ReadonlyArray<FlatRbacAction> = (() => {
	const out: Array<FlatRbacAction> = [];
	walk(RbacActions, [], out);
	out.sort((a, b) => a.pathLabel.localeCompare(b.pathLabel));
	return out;
})();

export function getAllRbacActions(): ReadonlyArray<FlatRbacAction> {
	return ALL_ACTIONS;
}

/**
 * Look up the display label for a given action string. Falls back to the raw
 * action if it isn't in the registry (custom actions, removed actions, etc.)
 * so the UI still renders something.
 */
export function getRbacActionLabel(action: string): string {
	const match = ALL_ACTIONS.find((a) => a.action === action);
	return match?.pathLabel ?? action;
}
